import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { BusStop } from '../models/bus-stop';
import { BusStopSchema } from '../models/bus-stop-schema';
import { DefaultValue } from '../models/default-value';
import { Discount } from '../models/discount';
import { Kid } from '../models/kid';
import { Level } from '../models/level';
import { Package } from '../models/package';
import { PackageRequest } from '../models/package-request';
import { Parent } from '../models/parent';
import { Participation } from '../models/participation';
import { Product } from '../models/product';
import { Program } from '../models/program';
import { Sport } from '../models/sport';
import { TransportSchema } from '../models/transport-schema';
import { ageRangeToDB, ageRangeToView, dateToDB, dateToView } from '../shared/utils';

@Injectable({
  providedIn: 'root'
})
export class ProgramsService {
  private readonly baseUrl = `${environment.api}/programs`;

  constructor(private http: HttpClient) { }

  public initialData = async (): Promise<{ programs: Program[], sports: Sport[], levels: Level[], defaults: DefaultValue[], schemas: TransportSchema[] }> => {
    const result = await firstValueFrom(this.http.get<{ programs: Program[], sports: Sport[], levels: Level[], defaults: DefaultValue[], schemas: TransportSchema[] }>(`${this.baseUrl}/initial-data`));
    for (const d of result.defaults) {
      d.ageRange = ageRangeToView((d as any).minAge, (d as any).maxAge);
    }

    return result;
  }

  public packageData = async (): Promise<{ programs: Program[], sports: Sport[], levels: Level[] }> => {
    const result = await firstValueFrom(this.http.get<{ programs: Program[], sports: Sport[], levels: Level[] }>(`${this.baseUrl}/package-data`));
    for (const program of result.programs) {
      this.programToView(program);
    }
    return result;
  }

  public updateProgramById = async (program: string): Promise<{ program: Program, participations: Participation[] }> => {
    const result = await firstValueFrom(this.http.get<{ program: Program, participations: Participation[] }>(`${this.baseUrl}/update-by-program-id/${program}`));
    this.programToView(result.program);
    for (const p of result.participations) {
      p.date = dateToView(p.date)!;
    }
    return result;
  }

  public enrollmentData = async (): Promise<{ kids: Kid[], parents: Parent[], stops: BusStop[] }> => {
    const result = await firstValueFrom(this.http.get<{ kids: Kid[], parents: Parent[], stops: BusStop[] }>(`${this.baseUrl}/enrollment-data`));
    for (const kid of result.kids) {
      kid.birthDate = dateToView(kid.birthDate);
    }
    for (const parent of result.parents) {
      parent.kids = parent.kids?.map(kid => result.kids.find(k => k.id === kid.id)!) ?? [];
    }

    return result;
  }

  public byId = async (programId: string): Promise<Program> => {
    const program = await firstValueFrom(this.http.get<Program>(`${this.baseUrl}/${programId}`));
    this.programToView(program);
    return program;
  }

  public delete = async (data: { id: string, deleted: boolean }): Promise<Program> => {
    return await firstValueFrom(this.http.post<Program>(`${this.baseUrl}/deleted`, { data }));
  }

  public addProgram = async (data: any) => {
    this.programToDB(data);
    const program = await firstValueFrom(this.http.post<Program>(`${this.baseUrl}/add`, { data }));
    this.programToView(program);
    return program;
  }

  public updateProgram = async (data: any) => {
    this.programToDB(data);
    const program = await firstValueFrom(this.http.post<Program>(`${this.baseUrl}/update`, { data }));
    this.programToView(program);
    return program;
  }

  public periodHasPackages = async (periodId: string): Promise<boolean> => {
    const packages = await firstValueFrom(this.http.get<number>(`${this.baseUrl}/periods/${periodId}`));
    return packages < 1;
  }

  public deletePeriod = async (periodId: string): Promise<boolean> => {
    return await firstValueFrom(this.http.delete<boolean>(`${this.baseUrl}/periods/${periodId}`))
  }

  public saveProgramsOrder = async (data: { id: string, order: number }[]): Promise<Program[]> => {
    data = data.sort((x, y) => x.order - y.order);
    for (let i = 0; i < data.length; i++) {
      const element = data[i];
      element.order = i;
    }
    return await firstValueFrom(this.http.post<Program[]>(`${this.baseUrl}/order`, { data }));
  }

  public addDiscount = async (data: any) => {
    data.date = dateToDB(data.date);
    const discount = await firstValueFrom(this.http.post<Discount>(`${this.baseUrl}/discount/add`, { data }));
    discount.date = dateToView(discount.date);
    return discount;
  }

  public updateDiscount = async (data: any) => {
    data.date = dateToDB(data.date);
    const discount = await firstValueFrom(this.http.post<Discount>(`${this.baseUrl}/discount/update`, { data }));
    discount.date = dateToView(discount.date);
    return discount;
  }

  public addPackage = async (parts: {
    period: string,
    date: string,
    netPrice: number,
    grossPrice: number,
    discount: number,
    depositAmount: number,
    depositDueDate: string
  }[], program: string, kid: string): Promise<Package[]> => {
    kid = kid.replaceAll("-", "");
    const packages = await firstValueFrom(this.http.post<Package[]>(
      `${this.baseUrl}/package/add`,
      { parts, program, kid }
    ));

    for (const p of packages) {
      p.depositDueDate = dateToView(p.depositDueDate)!;
      for (const pt of p.participations) {
        pt.date = dateToView(pt.date)!;
      }
    }
    return packages;
  }

  public packageRequestAll = async (): Promise<{ packages: PackageRequest[], programs: Program[] }> => {
    const result = await firstValueFrom(this.http.get<{ packages: PackageRequest[], programs: Program[] }>(`${this.baseUrl}/package-request/all`));
    for (const packageRequest of result.packages) {
      packageRequest.dates = packageRequest.dates.toString().split(';').map(d => dateToView(d)!);
      packageRequest.kidBirth = dateToView(packageRequest.kidBirth)!;
    }
    return result;
  }

  public packageRequestToDone = async (id: string): Promise<boolean> => {
    return firstValueFrom(this.http.put<boolean>(`${this.baseUrl}/package-request/done`, { id }));
  }

  public packageRequestToNew = async (id: string): Promise<boolean> => {
    return firstValueFrom(this.http.put<boolean>(`${this.baseUrl}/package-request/new`, { id }));
  }

  public packageRequestToDeleted = async (id: string): Promise<boolean> => {
    return firstValueFrom(this.http.put<boolean>(`${this.baseUrl}/package-request/deleted`, { id }));
  }

  public packageRequestToArchived = async (id: string): Promise<boolean> => {
    return firstValueFrom(this.http.put<boolean>(`${this.baseUrl}/package-request/archived`, { id }));
  }

  public packageRequestInfo = async (id: string): Promise<{ package: PackageRequest, busStop: BusStopSchema, sport: string, level: string, program: string }> => {
    const result = await firstValueFrom(this.http.get<{ package: PackageRequest, busStop: BusStopSchema, sport: string, level: string, program: string }>(`${this.baseUrl}/package-request-info/${id}`));
    result.package.dates = result.package.dates.toString().split(';').map(d => dateToView(d)!);
    result.package.date = dateToView(result.package.date);
    result.package.kidBirth = dateToView(result.package.kidBirth)!;
    return result;
  }

  public productById = async (productId: string): Promise<Product> => {
    return await firstValueFrom(this.http.get<Product>(`${this.baseUrl}/product/${productId}`));
  }

  private programToView = (program: Program | undefined): void => {
    if (!program) return;

    for (const period of program.periods ?? []) {
      period.dates = (period.dates.toString()).split(';').map(d => dateToView(d)!);
      period.ageRange = ageRangeToView((period as any).minAge, (period as any).maxAge);
    }
    for (const discount of program.discounts ?? []) {
      discount.date = dateToView(discount.date);
    }
  }

  private programToDB = (program: any | undefined): void => {
    if (!program) return;

    for (let period of program.periods ?? []) {
      period.dates = period.dates.map((d: any) => dateToDB(d.toDateString())!);
      const minMax = ageRangeToDB(period.ageRange)
      period.minAge = minMax.minAge;
      period.maxAge = minMax.maxAge;
    }
    for (const discount of program.discounts ?? []) {
      discount.date = dateToDB(discount.date?.toString());
    }

  }
}
